/**
 * Cross-module X XWP-74 (XO-14, XD-224; CP §11, §15.20) — the « nouvelle fenêtre » flow, React side.
 *
 * Source side — a form with work in progress offers a supporting page:
 *
 *   const fenetre = useOuvrirFenetre({ phrase: PHRASES_SOURCE.ACHAT });
 *   fenetre.peutOuvrir("/stock")                          // routeAccess for the session's role
 *   fenetre.demander({ chemin: stockProduitPath(id) })    // asks, then opens in a new window
 *   fenetre.demander({ chemin, ici: { libelle, run } })   // + a same-window choice (a round trip
 *                                                         //   that keeps the form, e.g. S1's bridge)
 *
 * The question is the shell's one dialog (components/shell/OuvrirFenetreDialog.jsx); the
 * unsaved-changes guard asks it too when a menu link of a dirty form points to a supporting page.
 *
 * Opened side — the shell's band (components/shell/RetourFenetrePrecedente.jsx) reads
 * `useFenetreSource()`: `actif` when this window was opened by the flow, `retourner()` focuses the source
 * window (and closes this one when it holds nothing unsaved), else says in one sentence that the source
 * is gone. Every other screen may call the same hook (WP-94, WP-111 return after a save).
 */
import * as React from "react";
import { useAuthStore } from "@/stores/authStore.js";
import { useFenetreSourceStore } from "@/stores/fenetreSourceStore.js";
import { notifyToast } from "@/hooks/useShortcuts.js";
import {
  capturerContexte,
  cheminSource,
  etatSource,
  libelleSource,
  ouvrirFenetre,
  pageSupport,
  peutOuvrir,
  retournerVersSource,
} from "@/lib/fenetre-source.js";
import { aDesModificationsNonEnregistrees } from "@/lib/modifications-en-cours.js";
import { M_FENETRE, PHRASES_SOURCE } from "@/config/fenetre-source.messages.js";
import { APP_TITLE } from "@/config/ui.config.js";

const fenetreCourante = () => (typeof window !== "undefined" ? window : null);
const roleCourant = () => useAuthStore.getState().user?.role ?? null;

function sessionDe(win) {
  try {
    return win?.sessionStorage ?? null;
  } catch {
    return null;
  }
}

// ── Source side ─────────────────────────────────────────────────────────────────────────────────

/**
 * Post the question « Ouvrir <page> dans une nouvelle fenêtre ? ». Refused (a toast, nothing posted)
 * when the session's role may not open the page (routeAccess, fail closed).
 * @param {{ chemin: string, phrase?: string, ici?: { libelle: string, run: () => void } | null,
 *   page?: string | null, role?: string | null }} d
 * @returns {boolean} whether the question was posted
 */
export function demanderNouvelleFenetre({ chemin, phrase = PHRASES_SOURCE.DEFAUT, ici = null, page = null, role = roleCourant() }) {
  const libelle = page ?? pageSupport(chemin)?.libelle ?? M_FENETRE.pageParDefaut;
  if (!peutOuvrir(role, chemin)) {
    notifyToast(M_FENETRE.interdit(libelle), "warning");
    return false;
  }
  useFenetreSourceStore.getState().poser({ chemin, page: libelle, phrase, ici });
  return true;
}

/**
 * The dialog's primary action: open the asked page in a new window. A blocked popup keeps the
 * question open with the browser sentence.
 */
export function confirmerNouvelleFenetre(win = fenetreCourante()) {
  const store = useFenetreSourceStore.getState();
  const d = store.demande;
  if (!d || !win) return null;
  const r = ouvrirFenetre({ win, chemin: d.chemin });
  if (!r.ok) {
    store.signalerErreur(M_FENETRE.bloquee);
    return r;
  }
  store.fermer();
  return r;
}

/** The dialog's same-window choice (only when the caller offered one). */
export function choisirIci() {
  const store = useFenetreSourceStore.getState();
  const ici = store.demande?.ici;
  store.fermer();
  ici?.run?.();
}

/**
 * Source-side handle of a screen.
 * @param {{ phrase?: string }} [opts] the sentence that says the form stays open (PHRASES_SOURCE)
 */
export function useOuvrirFenetre({ phrase = PHRASES_SOURCE.DEFAUT } = {}) {
  const role = useAuthStore((s) => s.user?.role ?? null);
  return React.useMemo(
    () => ({
      peutOuvrir: (chemin) => peutOuvrir(role, chemin),
      demander: ({ chemin, ici = null, page = null, phrase: p = phrase } = {}) =>
        demanderNouvelleFenetre({ chemin, ici, page, phrase: p, role }),
    }),
    [role, phrase],
  );
}

// ── Opened side ─────────────────────────────────────────────────────────────────────────────────

let contexte; // captured once per page load (the first render of the shell), before any page reads storage

/** This window's source context ({ jeton, nouveau }), captured once per page load. */
export function contexteFenetre(win = fenetreCourante()) {
  if (contexte === undefined) {
    contexte = win
      ? capturerContexte({ search: win.location?.search ?? "", storage: sessionDe(win) })
      : { jeton: null, nouveau: false };
  }
  return contexte;
}

/** Tests only: forget the captured context. */
export function reinitialiserContexteFenetre() {
  contexte = undefined;
}

function lireSource(win, jeton) {
  if (!jeton || !win) return { etat: "absente", libelle: null, chemin: null };
  return {
    etat: etatSource({ win, jeton }),
    libelle: libelleSource({ win, jeton, appTitle: APP_TITLE }),
    chemin: cheminSource({ win, jeton }),
  };
}

/**
 * The opened window's view of its source.
 * @returns {{ actif: boolean, etat: "ouverte"|"fermee"|"absente", libelle: string|null,
 *   message: string|null, retourner: () => any }}
 */
export function useFenetreSource() {
  const win = fenetreCourante();
  const [{ jeton }] = React.useState(() => contexteFenetre(win));
  const role = useAuthStore((s) => s.user?.role ?? null);
  const [source, setSource] = React.useState(() => lireSource(win, jeton));
  const [message, setMessage] = React.useState(null);

  // The source may close, or move to another page, while this window is in the background.
  React.useEffect(() => {
    if (!jeton || !win) return undefined;
    const rafraichir = () => setSource(lireSource(win, jeton));
    win.addEventListener("focus", rafraichir);
    win.document?.addEventListener?.("visibilitychange", rafraichir);
    return () => {
      win.removeEventListener("focus", rafraichir);
      win.document?.removeEventListener?.("visibilitychange", rafraichir);
    };
  }, [jeton, win]);

  const retourner = React.useCallback(() => {
    if (!win) return null;
    const modifie = aDesModificationsNonEnregistrees();
    const r = retournerVersSource({ win, jeton, modifie });
    if (!r.ok) {
      setSource((s) => ({ ...s, etat: r.raison, libelle: null }));
      setMessage(M_FENETRE.sourceFermee);
    } else if (!r.fermee) {
      setMessage(modifie ? M_FENETRE.resteOuverte : null);
    }
    return r;
  }, [win, jeton]);

  // « offered only when routeAccess admits the role » — a source page the role cannot open (a role
  // change in the shared session) is not offered; an unreachable source still shows the fallback.
  const accessible = !source.chemin || peutOuvrir(role, source.chemin);
  const actif = Boolean(jeton) && Boolean(role) && accessible;
  return {
    actif,
    etat: source.etat,
    libelle: source.libelle,
    message: message ?? (actif && source.etat !== "ouverte" ? M_FENETRE.sourceFermee : null),
    retourner,
  };
}
